/**
 * Content section for OfflineMeeting detail view.
 * Shows title, description, location, remaining seats, schedule info.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { OfflineMeetingEntry } from '../../../Model/Explore/OfflineMeetingEntry';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { MarkdownRenderer } from '../../Markdown/MarkdownRenderer';
import { Theme } from '../../../Theme/Theme';
import dayjs from 'dayjs';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleContainer = styled.div``;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const GradientLine = styled.div`
  width: 48px;
  height: 3px;
  background: ${Theme.Colors.primary};
  border-radius: 2px;
`;

const Description = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: ${Theme.Colors.charcoal};

  p {
    margin: 0 0 16px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: ${Theme.Colors.primary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
`;

const InfoIcon = styled.i`
  font-size: 18px;
  color: ${Theme.Colors.primary};
  margin-top: 2px;
  min-width: 20px;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${Theme.Colors.darkGrey};
`;

const InfoValue = styled.div`
  font-size: 0.95rem;
  color: ${Theme.Colors.textColor};
`;

const SeatsTag = styled.span<{ warning?: boolean }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${({ warning }) => (warning ? '#FFF3E0' : '#E8F5E9')};
  color: ${({ warning }) => (warning ? '#E65100' : '#2E7D32')};
`;

const AlertCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #FFF3E0;
  border-radius: 12px;
`;

const AlertText = styled.span`
  font-size: 0.9rem;
  color: #E65100;
`;

export type ExploreContentViewOfflineMeetingContentProps = {
  entry: OfflineMeetingEntry;
};

export const ExploreContentViewOfflineMeetingContent: React.FC<ExploreContentViewOfflineMeetingContentProps> =
  observer(({ entry }) => {
    const title = useLocalized(entry.name);
    const content = useLocalized(entry.description);
    const quote = useLocalized(entry.quotes);
    const { t } = useTrans();

    const showLocation = !!entry.location.name || !!entry.location.address;
    const remainingSeats = entry.remainingSeats;
    const hasSeats = entry.hasRemainingSeats;

    return (
      <ContentContainer>
        <TitleContainer>
          <Title>{title}</Title>
          <GradientLine />
        </TitleContainer>

        {!entry.canSubscribe && (
          <AlertCard>
            <i className="bi bi-exclamation-triangle" style={{ color: '#E65100', fontSize: 18 }} />
            <AlertText>
              {t('exploreModal.offlineMeeting.registrationUnavailable', {
                defaultValue: 'Registration is no longer available for this meeting.',
              })}
            </AlertText>
          </AlertCard>
        )}

        {entry.maxParticipants > 0 && (
          <div>
            <SeatsTag warning={remainingSeats <= 3 && remainingSeats > 0}>
              {remainingSeats > 0
                ? t('exploreModal.offlineMeeting.remainingSeats', {
                    defaultValue: '{{remainingSeats}} seats left',
                    remainingSeats,
                  })
                : t('exploreModal.offlineMeeting.noSeats', { defaultValue: 'Fully booked' })}
            </SeatsTag>
          </div>
        )}

        {!!quote && (
          <Description style={{ fontStyle: 'italic', borderLeft: `3px solid ${Theme.Colors.primary}`, paddingLeft: 16 }}>
            {quote}
          </Description>
        )}

        <Description>
          <MarkdownRenderer text={content} />
        </Description>

        {showLocation && (
          <InfoRow>
            <InfoIcon className="bi bi-geo-alt" />
            <InfoContent>
              <InfoLabel>
                {t('exploreModal.offlineMeeting.location', { defaultValue: 'Location' })}
              </InfoLabel>
              <InfoValue>
                {[entry.location.name, entry.location.address].filter(Boolean).join(', ')}
              </InfoValue>
            </InfoContent>
          </InfoRow>
        )}

        {entry.registerUntilDate && (
          <InfoRow>
            <InfoIcon className="bi bi-clock" />
            <InfoContent>
              <InfoLabel>
                {t('exploreModal.offlineMeeting.registerUntil', { defaultValue: 'Register until' })}
              </InfoLabel>
              <InfoValue>{dayjs(entry.registerUntilDate).format('LLL')}</InfoValue>
            </InfoContent>
          </InfoRow>
        )}

        {entry.language && (
          <InfoRow>
            <InfoIcon className="bi bi-translate" />
            <InfoContent>
              <InfoLabel>
                {t('exploreModal.offlineMeeting.language', { defaultValue: 'Language' })}
              </InfoLabel>
              <InfoValue>
                {t(`globals.language.${entry.language}`, { defaultValue: entry.language })}
              </InfoValue>
            </InfoContent>
          </InfoRow>
        )}

        {entry.recurringPattern && (
          <InfoRow>
            <InfoIcon className="bi bi-calendar-event" />
            <InfoContent>
              <InfoLabel>
                {t('exploreModal.offlineMeeting.schedule', { defaultValue: 'Schedule' })}
              </InfoLabel>
              <InfoValue>
                {entry.recurringPattern.asText(entry.nextDate?.format('LT') ?? '') ?? ''}
              </InfoValue>
            </InfoContent>
          </InfoRow>
        )}
      </ContentContainer>
    );
  });
